/**
 * **Raw Data's filter panel — RD-18 · RD-19, `docs/EDITS-2026-09-24.md` §5.**
 *
 * One row of controls, built from the filters the *current dataset* answers
 * (`DatasetCard.filters`, which the server derives from the same registry that
 * builds its SQL). Every filter is multi-value.
 *
 * **A filter that does not apply is shown, greyed, with the reason — never
 * silently dropped.** The URL keeps it, so switching back to a dataset that
 * answers it brings it back into force; and the server refuses an inapplicable
 * filter with a 400 anyway, so the page never sends one.
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'

import { api, type AssetHit, type DatasetCard, type FilterCard, type RawFilters } from '../lib/api'
import { formatDate } from '../lib/dates'
import { MAX_SELECTED_ASSETS, RAW_FILTER_KEYS } from '../lib/rawFilters'

const LABELS: Record<string, string> = {
  channel: 'Channel',
  asset_type: 'Asset Type',
  assets: 'Stories & Spotlights',
  metric: 'Metric',
  dimension: 'Breakdown',
  status: 'Status',
  match_status: 'Match Status',
  classification: 'Original / Re-Run',
  author: 'Author',
  air_time: 'Air Time',
  episode: 'Episode #',
  name: 'Name',
  task: 'ClickUp Task',
}

const TYPE_LABELS: Record<string, string> = {
  STORY: 'Public Story',
  SAVED_STORY: 'Saved Story',
  SPOTLIGHT: 'Spotlight',
}

const control = {
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
} as const

export function RawDataFilters({
  card,
  filters,
  onChange,
}: {
  card: DatasetCard
  filters: RawFilters
  onChange: (next: RawFilters) => void
}) {
  const options = useQuery({
    queryKey: ['raw-filter-options', card.key],
    queryFn: () => api.filterOptions(card.key),
    staleTime: 5 * 60_000,
  })

  const set = (key: string, values: string[]) => {
    const next = { ...filters }
    if (values.length) next[key] = values
    else delete next[key]
    onChange(next)
  }

  const applicable = new Set(card.filters.map((spec) => spec.key))
  const inapplicable = RAW_FILTER_KEYS.filter(
    (key) => !applicable.has(key) && (filters[key]?.length ?? 0) > 0,
  )
  const active = Object.values(filters).some((values) => values.length)

  if (!card.filters.length && !inapplicable.length) return null

  return (
    <div
      className="mb-3 flex flex-wrap items-center gap-2"
      role="group"
      aria-label={`Filter ${card.label}`}
    >
      {card.filters.map((spec) => (
        <FilterControl
          key={spec.key}
          spec={spec}
          values={filters[spec.key] ?? []}
          options={options.data?.[spec.key] ?? []}
          onChange={(values) => set(spec.key, values)}
        />
      ))}

      {inapplicable.map((key) => (
        <span
          key={key}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs"
          style={{ ...control, color: 'var(--text-label)', borderStyle: 'dashed' }}
          title={`Not Applicable: ${card.label} Cannot Be Filtered by ${LABELS[key] ?? key}. Kept for Datasets That Can.`}
        >
          <s>
            {LABELS[key] ?? key}: {filters[key]!.length}
          </s>
          <span>· Not Applicable Here</span>
        </span>
      ))}

      {active && (
        <button
          type="button"
          className="px-2 py-1 text-xs"
          onClick={() => onChange({})}
          style={{ background: 'none', border: 'none', color: 'var(--brand-ink)', cursor: 'pointer' }}
        >
          Clear Filters
        </button>
      )}
    </div>
  )
}

function FilterControl({
  spec,
  values,
  options,
  onChange,
}: {
  spec: FilterCard
  values: string[]
  options: { value: string; label: string }[]
  onChange: (values: string[]) => void
}) {
  if (spec.kind === 'assets') return <AssetPicker values={values} onChange={onChange} />
  if (spec.kind === 'search') return <SearchFilter label={spec.label} values={values} onChange={onChange} />
  if (spec.kind === 'text') return <TextFilter label={spec.label} values={values} onChange={onChange} />
  return <MultiSelect label={spec.label} values={values} options={options} onChange={onChange} />
}

/** A popover of checkboxes. Closes on Esc and on a click outside. */
function Popover({
  label,
  summary,
  children,
}: {
  label: string
  summary: ReactNode
  children: ReactNode
}) {
  const [open, setOpen] = useState(false)
  const root = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  return (
    <div
      ref={root}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape') setOpen(false)
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => setOpen((value) => !value)}
        className="inline-flex items-center gap-1 px-2 py-1 text-xs"
        style={control}
      >
        <span style={{ color: 'var(--text-label)' }}>{label}:</span> {summary} ▾
      </button>
      {open && (
        <div
          className="absolute left-0 mt-1 p-2"
          style={{
            ...control,
            zIndex: 'var(--z-dropdown)' as unknown as number,
            boxShadow: 'var(--shadow-pop)',
            minWidth: '14rem',
            maxHeight: '18rem',
            overflowY: 'auto',
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

function MultiSelect({
  label,
  values,
  options,
  onChange,
}: {
  label: string
  values: string[]
  options: { value: string; label: string }[]
  onChange: (values: string[]) => void
}) {
  const chosen = new Set(values)
  const summary = !values.length
    ? 'All'
    : values.length === 1
      ? (options.find((option) => option.value === values[0])?.label ?? values[0])
      : `${values.length} Selected`

  return (
    <Popover label={label} summary={summary}>
      {options.length === 0 ? (
        <p className="text-xs" style={{ color: 'var(--text-label)' }}>
          Loading…
        </p>
      ) : (
        options.map((option) => (
          <label key={option.value} className="flex items-center gap-2 py-0.5 text-sm">
            <input
              type="checkbox"
              checked={chosen.has(option.value)}
              onChange={(event) =>
                onChange(
                  event.target.checked
                    ? [...values, option.value]
                    : values.filter((value) => value !== option.value),
                )
              }
            />
            {option.label}
          </label>
        ))
      )}
    </Popover>
  )
}

/** Comma-separated values, committed on Enter or blur. */
function TextFilter({
  label,
  values,
  onChange,
}: {
  label: string
  values: string[]
  onChange: (values: string[]) => void
}) {
  const [draft, setDraft] = useState(values.join(', '))
  useEffect(() => setDraft(values.join(', ')), [values])
  const commit = () =>
    onChange(
      [...new Set(draft.split(',').map((value) => value.trim()).filter(Boolean))],
    )
  return (
    <label className="inline-flex items-center gap-1 text-xs">
      <span style={{ color: 'var(--text-label)' }}>{label}:</span>
      <input
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit()
        }}
        placeholder={label === 'Episode #' ? 'e.g. 583F, 584F' : 'Paste an ID'}
        className="px-2 py-1 text-xs"
        style={{ ...control, width: '9rem' }}
      />
    </label>
  )
}

function hitLabel(hit: AssetHit): string {
  const title = hit.title || 'Untitled'
  return `${title} · ${hit.display_label} · ${TYPE_LABELS[hit.asset_type] ?? hit.asset_type}`
}

/**
 * **RD-19 — the story picker.** Type-ahead over title, episode #, ClickUp
 * author or asset ID; each pick becomes a removable chip. The chips for IDs
 * that arrived in the URL are resolved back into titles, so a click-through
 * does not land on a row of opaque IDs.
 */
function AssetPicker({
  values,
  onChange,
}: {
  values: string[]
  onChange: (values: string[]) => void
}) {
  const [term, setTerm] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 250)
    return () => clearTimeout(timer)
  }, [term])

  const chips = useQuery({
    queryKey: ['raw-asset-chips', values.join(',')],
    queryFn: () => api.assetSearch({ ids: values }),
    enabled: values.length > 0,
    staleTime: 5 * 60_000,
  })
  const results = useQuery({
    queryKey: ['raw-asset-search', debounced],
    queryFn: () => api.assetSearch({ q: debounced }),
    enabled: debounced.length >= 2,
  })

  const known = new Map((chips.data ?? []).map((hit) => [hit.asset_id, hit]))
  const full = values.length >= MAX_SELECTED_ASSETS

  return (
    <div className="flex flex-wrap items-center gap-1">
      <span className="text-xs" style={{ color: 'var(--text-label)' }}>
        Stories &amp; Spotlights:
      </span>
      {values.map((id) => {
        const hit = known.get(id)
        return (
          <span
            key={id}
            className="inline-flex items-center gap-1 px-2 py-0.5 text-xs"
            style={{ ...control, background: 'var(--brand-tint)', color: 'var(--brand-ink)' }}
            title={id}
          >
            {hit ? hitLabel(hit) : <span className="is-mono">{id.slice(0, 8)}…</span>}
            <button
              type="button"
              aria-label={`Remove ${hit?.title ?? id}`}
              onClick={() => onChange(values.filter((value) => value !== id))}
              style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}
            >
              ×
            </button>
          </span>
        )
      })}
      <div className="relative">
        <input
          type="search"
          value={term}
          disabled={full}
          onChange={(event) => setTerm(event.target.value)}
          placeholder={
            full ? `Up to ${MAX_SELECTED_ASSETS}` : 'Search Title, Episode #, Author or ID'
          }
          aria-label="Search Stories and Spotlights"
          className="px-2 py-1 text-xs"
          style={{ ...control, width: '16rem' }}
        />
        {debounced.length >= 2 && term && (
          <ul
            className="absolute left-0 mt-1 p-1"
            role="listbox"
            aria-label="Matching Stories and Spotlights"
            style={{
              ...control,
              zIndex: 'var(--z-dropdown)' as unknown as number,
              boxShadow: 'var(--shadow-pop)',
              width: '26rem',
              maxHeight: '18rem',
              overflowY: 'auto',
              listStyle: 'none',
            }}
          >
            {results.isLoading && (
              <li className="px-2 py-1 text-xs" style={{ color: 'var(--text-label)' }}>
                Searching…
              </li>
            )}
            {results.data?.length === 0 && (
              <li className="px-2 py-1 text-xs" style={{ color: 'var(--text-label)' }}>
                Nothing Matches.
              </li>
            )}
            {results.data?.map((hit) => {
              const picked = values.includes(hit.asset_id)
              return (
                <li key={hit.asset_id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={picked}
                    disabled={picked}
                    onClick={() => {
                      onChange([...values, hit.asset_id])
                      setTerm('')
                    }}
                    className="block w-full px-2 py-1 text-left text-xs"
                    style={{
                      background: 'none',
                      border: 'none',
                      color: picked ? 'var(--text-label)' : 'var(--text-primary)',
                      cursor: picked ? 'default' : 'pointer',
                    }}
                  >
                    <strong>{hit.title || 'Untitled'}</strong>
                    <span style={{ color: 'var(--text-secondary)' }}>
                      {' '}
                      · {hit.display_label} · {TYPE_LABELS[hit.asset_type]} ·{' '}
                      {formatDate(hit.published_at)}
                      {hit.episode_number ? ` · #${hit.episode_number}` : ''}
                      {hit.author ? ` · ${hit.author}` : ''}
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

/**
 * RD-22 — part of a name, any case, as one term. Commas are part of what a
 * reader might type in a title, so unlike `TextFilter` this never splits.
 * Committed on Enter or blur so the table does not refetch per keystroke.
 */
function SearchFilter({
  label,
  values,
  onChange,
}: {
  label: string
  values: string[]
  onChange: (values: string[]) => void
}) {
  const [draft, setDraft] = useState(values[0] ?? '')
  useEffect(() => setDraft(values[0] ?? ''), [values])
  const commit = () => onChange(draft.trim() ? [draft.trim()] : [])
  return (
    <label className="inline-flex items-center gap-1 text-xs">
      <span style={{ color: 'var(--text-label)' }}>{label}:</span>
      <input
        type="search"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') commit()
        }}
        placeholder="e.g. Maiden Name"
        aria-label={`Search ${label}`}
        className="px-2 py-1 text-xs"
        style={{ ...control, width: '12rem' }}
      />
    </label>
  )
}
